// Drill chimes. Call primeAudioContext() from a user-gesture tap (autoplay policy).

let ctx = null

export function primeAudioContext () {
  if (typeof window === 'undefined') return
  var Ctor = window.AudioContext || window.webkitAudioContext
  if (!Ctor) return

  // Rebuild here, the only gesture-time call: iOS ignores resume() outside a
  // gesture, so an 'interrupted' context can't recover elsewhere (BUGFIX-003).
  // Close the old one first: iOS caps contexts per page.
  if (ctx && ctx.state !== 'running' && ctx.state !== 'suspended') {
    try { ctx.close() } catch (e) { /* already gone */ }
    ctx = null
  }
  if (!ctx) ctx = new Ctor()
  if (ctx.state !== 'running' && ctx.resume) ctx.resume()
}

// BUGFIX-004: iOS can report 'running' for a context that stopped rendering
// after backgrounding, so backgrounding (not ctx.state) triggers a rebuild.
export function releaseAudioContext () {
  if (!ctx) return
  try { ctx.close() } catch (e) { /* already gone */ }
  ctx = null
}

// Schedule only once 'running': iOS drops sounds scheduled before resume() resolves.
// Resolves once `fn` ran or never will (playSpeakCue gates the mic on it).
function whenRunning (fn) {
  if (!ctx) return Promise.resolve()
  if (ctx.state === 'running') {
    fn()
    return Promise.resolve()
  }
  // Never build a replacement here: outside a gesture iOS gives a silent
  // context and burns a slot. Losing one chime beats poisoning page audio.
  if (!ctx.resume) return Promise.resolve()
  return ctx.resume().then(function () {
    if (ctx && ctx.state === 'running') fn()
  }).catch(function () { /* silent until the next prime rebuilds it */ })
}

// Pitch scales exponentially with score: 80% -> C6, 100% -> C7.
export function playPassSound (accuracy) {
  whenRunning(function () {
    var score = Math.max(80, Math.min(100, accuracy || 80))
    var t = (score - 80) / 20                                 // 0..1
    var bodyFreq = 1046.5 * Math.pow(2, t)                   // C6..C7
    var decay = 0.45 + t * 0.2                               // 0.45s..0.65s

    var now = ctx.currentTime

    function strike (freq, peakGain, dur) {
      var osc = ctx.createOscillator()
      var gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(peakGain, now)
      gain.gain.exponentialRampToValueAtTime(0.001, now + dur)
      osc.start(now)
      osc.stop(now + dur + 0.05)
    }

    strike(bodyFreq,         0.3,  decay)
    strike(bodyFreq * 2.67,  0.07, decay * 0.3)  // inharmonic shimmer
  })
}

const SPEAK_CUE_MS = 160

// Rising "your turn" blip. Resolves after the tone (timed from whenRunning) so
// the mic never records it (BUGFIX-003, BUGFIX-004).
export function playSpeakCue () {
  return whenRunning(function () {
    var now = ctx.currentTime
    var osc = ctx.createOscillator()
    var gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(523, now)
    osc.frequency.exponentialRampToValueAtTime(784, now + 0.1)
    gain.gain.setValueAtTime(0.16, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14)
    osc.start(now)
    osc.stop(now + 0.16)
  }).then(function () {
    return new Promise(function (resolve) { setTimeout(resolve, SPEAK_CUE_MS) })
  })
}

export function playEndSound () {
  whenRunning(function () {
    var now = ctx.currentTime
    var osc = ctx.createOscillator()
    var gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, now)
    osc.frequency.exponentialRampToValueAtTime(587, now + 0.1)
    gain.gain.setValueAtTime(0.16, now)
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14)
    osc.start(now)
    osc.stop(now + 0.16)
  })
}
